import { MRT_RowVirtualizer, MRT_TableInstance } from 'material-react-table'
import { RefObject } from 'react'
import { v4 } from 'uuid'

import { AddNewRowProps } from '@/components/NewTable/types'
import { renumberOrderInPlace } from '@/utils/collectionOrder'

// Reusable helpers to avoid DOM-query duplication
const queryFirstEditableInput = (
	container?: HTMLElement | null,
): HTMLInputElement | null => {
	const table =
		(container as HTMLTableElement) ??
		document.querySelector<HTMLTableElement>('table.MuiTable-root')
	if (!table) return null

	const rows = table.querySelectorAll<HTMLTableRowElement>('.MuiTableRow-root')
	if (!rows.length) return null

	const lastRow = rows[rows.length - 1]
	const cells =
		lastRow.querySelectorAll<HTMLTableCellElement>('.MuiTableCell-root')
	const targetCell = cells.length > 1 ? cells[1] : cells[0]
	return targetCell?.querySelector<HTMLInputElement>('input') ?? null
}

const tryFocusFirstEditableInput = (
	container?: HTMLElement | null,
): boolean => {
	const input = queryFirstEditableInput(container)
	if (!input) return false
	input.focus()
	// select may not exist on some inputs; guard it
	input.select?.()
	return true
}

export const focusOnFirstEditableCell = (
	focusDelay: number = 100,
	tableInstance?: MRT_TableInstance<any>,
	rowVirtualizerRef?: RefObject<MRT_RowVirtualizer>,
) => {
	return setTimeout(() => {
		try {
			const isVirtualized =
				!!tableInstance?.options.enableRowVirtualization &&
				!!rowVirtualizerRef?.current
			const rowCount = tableInstance?.getRowModel().rows.length ?? 0
			const container = tableInstance?.refs?.tableContainerRef
				?.current as HTMLElement | null

			// If virtualized and we have rows, scroll the last row into view first
			if (isVirtualized && rowCount > 0) {
				const lastRowIndex = rowCount - 1
				rowVirtualizerRef?.current?.scrollToIndex(lastRowIndex, {
					align: 'end',
				})
				// Allow time for scroll + render, then focus
				setTimeout(() => {
					tryFocusFirstEditableInput(container)
				}, 100)
				return
			}

			// Non-virtualized or no rows: try to focus immediately
			tryFocusFirstEditableInput(container)
		} catch (error) {
			console.error('Error focusing on first editable cell:', error)
		}
	}, focusDelay)
}
export const addNewRow = <TData extends { id?: number; uuid?: string }>({
	updateTableData,
	newRowPayload,
	setSelectedRow,
	dynamicUuidProp,
	tableInstance,
	rowVirtualizerRef, // Add this parameter
}: AddNewRowProps<TData>) => {
	const { id: _id, ...payloadWithoutId } = newRowPayload || {}

	let createdRow: TData | undefined

	updateTableData?.((draft: TData[]) => {
		// Calculate next ID
		const highestId = draft.reduce((accId, draftRow) => {
			return Math.max(accId, draftRow?.id || 0)
		}, 0)

		const newRowId = highestId + 1
		// Create new row with correct ID
		const newRow = {
			id: newRowId,
			uuid: v4(),
			...(dynamicUuidProp && { [dynamicUuidProp]: v4() }),
			...payloadWithoutId,
		} as TData

		draft.push(newRow)
		renumberOrderInPlace(draft as { order?: number }[])
		createdRow = draft[draft.length - 1]
	})

	// Select after tableData/ref are updated — selecting inside the immer updater
	// races panel flush-on-switch against a stale snapshot and can drop the new row.
	if (createdRow) {
		setSelectedRow?.(createdRow)
	}

	// Pass the table instance to the focus function
	focusOnFirstEditableCell(500, tableInstance, rowVirtualizerRef)
}
